import React, { useContext, useMemo } from 'react';
import { IDirectorate, EntityValidations, Directorate, ISpecificEntityFormProps, IUser, CustomField, Contributor } from '../../types';
import { LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrDropdown } from '../cr/CrDropdown';
import { CrUserPicker } from '../cr/CrUserPicker';
import { CrTextField } from '../cr/CrTextField';
import { EntityForm } from '../EntityForm';
import { CrReportingCyclePicker } from '../cr/CrReportingCyclePicker';
import { ReportingCycleService } from '../../services/ReportingCycleService';
import { CrMultiDropdownWithText } from '../cr/CrMultiDropdownWithText';
import { DataContext } from '../DataContext';
import { IntegrationContext } from '../IntegrationContext';
import { OrbUserContext } from '../OrbUserContext';

class DirectorateValidations extends EntityValidations {
    public GroupID: string = null;
    public ReportingCycle: string = null;
}

export const DirectorateFormAdmin = (props: ISpecificEntityFormProps): React.ReactElement => {
    const { userPermissions, orbConfig } = useContext(OrbUserContext);
    const { disableDirectorateManagement } = useContext(IntegrationContext);
    const { dataServices, lookupData, loadLookupData: { customFieldDfns, entityStatuses, groups, userDirectorates, users: { all: allUsers } } } = useContext(DataContext);

    useMemo(() => customFieldDfns(), [customFieldDfns]);
    useMemo(() => entityStatuses(), [entityStatuses]);
    useMemo(() => groups(), [groups]);
    useMemo(() => userDirectorates(), [userDirectorates]);
    useMemo(() => allUsers(), [allUsers]);

    const validateEntity = (directorate: IDirectorate): Promise<DirectorateValidations> => {
        const errors = new DirectorateValidations();

        if (directorate.Title === null || directorate.Title === '') {
            errors.Title = `${orbConfig.OrgL2Label} name is required`;
            errors.Valid = false;
        }
        else {
            errors.Title = null;
        }

        if (directorate.GroupID === null) {
            errors.GroupID = `${orbConfig.OrgL1Label} is required`;
            errors.Valid = false;
        }
        else {
            errors.GroupID = null;
        }

        return Promise.resolve(errors);
    };

    const directorateUsers = (directorate: IDirectorate): IUser[] => {
        if (lookupData?.Users?.All && directorate.ID) {
            return lookupData.Users.All.filter(u => LookupService.userIsInDirectorate(lookupData, u.ID, directorate.ID)
                || LookupService.userIsContributor(directorate, u.ID)
                || u.ID === directorate.DirectorUserID
                || u.ID === directorate.ReportApproverUserID
                || u.ID === directorate.ReportingLeadUserID
            );
        }
        return [];
    };

    return (
        <EntityForm<IDirectorate, DirectorateValidations>
            {...props}
            entityName={orbConfig.OrgL2Label}
            renderFormFields={(changeHandlers, formState) => {
                const { FormData: directorate, ValidationErrors: errors } = formState;
                //const mappedUsers = directorateUsers(directorate);
                const users = /*directorate.ID ? mappedUsers :*/ lookupData.Users?.All;
                // const suggestions = directorate.ID ? {
                //     initialSuggestionsHeaderText: 'Directorate users',
                //     initialSuggestions: mappedUsers,
                //     noResultsFoundText: 'No users are mapped to this directorate'
                // } : {};
                return (
                    <div>
                        <CrTextField
                            label="Name"
                            maxLength={255}
                            required={true}
                            disabled={disableDirectorateManagement || !userPermissions.UserIsSystemAdmin()}
                            className={styles.formField}
                            value={directorate.Title}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Title')}
                            errorMessage={errors.Title}
                        />
                        <CrDropdown
                            label={orbConfig.OrgL1Label}
                            required={true}
                            disabled={disableDirectorateManagement || !userPermissions.UserIsSystemAdmin()}
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions(lookupData.Groups)}
                            selectedKey={directorate.GroupID}
                            onChange={(_, option, index) => changeHandlers.changeDropdown(option, 'GroupID', index)}
                            errorMessage={errors.GroupID}
                        />
                        <CrUserPicker
                            label={orbConfig.OrgMgrL2Lable}
                            className={styles.formField}
                            users={users}
                            selectedUsers={directorate.DirectorUserID && [directorate.DirectorUserID]}
                            onChange={u => changeHandlers.changeUserPicker(u, 'DirectorUserID')}
                            //{...suggestions}
                        />
                        <CrDropdown
                            label="Status"
                            className={styles.formField}
                            disabled={disableDirectorateManagement}
                            options={LookupService.entitiesToSelectableOptions(lookupData.EntityStatuses)}
                            selectedKey={directorate.EntityStatusID}
                            onChange={(_, o) => changeHandlers.changeStatusDropdown(o, 'EntityStatusID')}
                        />
                    </div>
                );
            }}
            loadEntity={directorateId => dataServices.directorateService.read(directorateId, true, true)}
            loadNewEntity={() => new Directorate()}
            loadEntityValidations={() => new DirectorateValidations()}
            onValidateEntity={validateEntity}
            onCreate={directorate => dataServices.directorateService.create(directorate)}
            onUpdate={directorate => dataServices.directorateService.update(directorate.ID, directorate)}
            parentEntities={dataServices.directorateService.parentEntities}
            childEntities={[
                {
                    ObjectParentProperty: 'Contributors',
                    ParentIdProperty: 'DirectorateID',
                    ChildIdProperty: 'ContributorUserID',
                    ChildService: dataServices.contributorService
                },
                {
                    ObjectParentProperty: 'CustomFields',
                    ParentIdProperty: 'DirectorateID',
                    ChildIdProperty: 'CustomFieldDefnID',
                    ChildService: dataServices.customFieldService
                }
            ]}
        />
    );
};
